import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Alert } from '@/components/ui/alert';

import Skeleton from 'react-loading-skeleton';
import { useTranslation } from 'react-i18next';
import {
  CalibrationParticipantType,
  CalibrationSessionState,
  useCalibrationSessionQuery,
} from '../../../__generated__/graphql';
import PageHeader from '../../../components/PageHeader';
import { useFlairBreadcrumbHook } from '../../../../../hooks/useFlairBreadcrumbHook';
import { routes } from '../../../routes';
import { CalibrationWorkspaceProvider } from './CalibrationWorkspaceContext';
import { CalibrationGrid } from './CalibrationGrid/CalibrationGrid';
import { CalibrationSaveBar } from './CalibrationSaveBar/CalibrationSaveBar';
import { useCalibrationSessionStateBadge } from '../useCalibrationSessionState';

type Props = {
  sessionId: string;
};

export const CalibrationWorkspace: React.FC<React.PropsWithChildren<Props>> = ({
  sessionId,
}) => {
  const { t } = useTranslation();
  const { data, loading, error, refetch } = useCalibrationSessionQuery({
    variables: { id: sessionId },
    fetchPolicy: 'cache-and-network',
  });

  const session = data?.calibrationSession;

  useFlairBreadcrumbHook(
    [
      { label: t('navigation.menuItems.personalDevelopment.title') },
      {
        label: t(
          'navigation.menuItems.personalDevelopment.subMenuItems.calibrations.title',
        ),
        url: routes.calibration.route.create({}),
      },
      { label: session?.Name ?? '' },
    ],
    loading && !data,
  );

  const { label: stateLabel, bg: stateBg } = useCalibrationSessionStateBadge(
    session?.flair__State__c,
  );

  if (loading && !data) {
    return (
      <div className="py-3">
        <Skeleton width={250} height={28} className="mb-3" />
        <Skeleton height={40} className="mb-2" />
        <Skeleton count={5} height={48} className="mb-2" />
      </div>
    );
  }

  if (error || !session) {
    return (
      <Alert variant="danger">
        {error?.message ?? 'Failed to load calibration session.'}
      </Alert>
    );
  }

  const isObserver =
    session.currentEmployeeParticipantType ===
    CalibrationParticipantType.Observer;
  const isFinished =
    session.flair__State__c !== CalibrationSessionState.InProgress;

  return (
    <CalibrationWorkspaceProvider session={session} refetchSession={refetch}>
      <PageHeader
        title={session.Name}
        leftComponent={<Badge variant={stateBg}>{stateLabel}</Badge>}
      />
      {isObserver && (
        <Alert variant="info">
          {t('performanceReview.calibration.workspace.readOnly')}
        </Alert>
      )}
      {isFinished && (
        <Alert variant="secondary">
          {t('performanceReview.calibration.workspace.finished')}
        </Alert>
      )}
      <CalibrationGrid />
      <CalibrationSaveBar />
    </CalibrationWorkspaceProvider>
  );
};
